import i18next from "i18next";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Label,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useGetEvolutionChart } from "../api/hooks/useRiskThemes";
import { useDesignSystem } from "../context/designSystemContext";
import { pSBC } from "../utils/pSBC";

export const ControlImplementationEvolution = ({
  year,
  priority,
  okr,
}: {
  year: number;
  priority: string[];
  okr: string[];
}) => {
  const { data } = useGetEvolutionChart(year, priority, okr);
  const { designSystem } = useDesignSystem();

  const monthNames = [
    i18next.t("jan"),
    i18next.t("feb"),
    i18next.t("mar"),
    i18next.t("apr"),
    i18next.t("may"),
    i18next.t("jun"),
    i18next.t("jul"),
    i18next.t("aug"),
    i18next.t("sep"),
    i18next.t("oct"),
    i18next.t("nov"),
    i18next.t("dec"),
  ];

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();

  const transformDataGraph = (
    data: Record<number, any>,
    year: number,
    currentYear: number,
    currentMonth: number
  ) => {
    return Object.entries(data).map(([key, values]) => {
      const monthIndex = parseInt(key) - 1;
      const isFutureMonth = year === currentYear && monthIndex > currentMonth;

      return {
        name: monthNames[monthIndex] + "/" + year.toString().slice(-2),
        NOT_STARTED: isFutureMonth ? 0 : values.NOT_STARTED,
        GOING: isFutureMonth ? 0 : values.GOING,
        FINISHED: isFutureMonth ? 0 : values.FINISHED,
        TOTAL_IMPLEMENTED: isFutureMonth
          ? null
          : (values.finishedPerc * 100).toFixed(0).toString(),
      };
    });
  };

  const transformedDataGraph = data
    ? transformDataGraph(data, year, currentYear, currentMonth)
    : [];

  return (
    <div className="mx-3 mt-5 mb-3">
      <div
        className="text-2xl font-openSans font-bold text-[16px] leading-[16px] tracking-[0.18px] mb-3 mx-2"
        style={{ color: designSystem?.cssBasics.primaryColor }}
      >
        {i18next.t("control_implementation_evolution")}
      </div>

      <div className="flex items-center gap-1">
        <div className="flex-grow">
          {/* nesse espaço vai ter o grafico com 4 barras */}
          <div className="flex items-end justify-between h-[230px] w-full">
            {transformedDataGraph && (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  width={500}
                  height={400}
                  data={transformedDataGraph}
                  margin={{
                    top: 1,
                    right: 1,
                    bottom: 1,
                    left: 1,
                  }}
                >
                  <CartesianGrid stroke="#f5f5f5" />

                  <XAxis dataKey="name" interval={0} angle={0} dy={1}></XAxis>

                  <YAxis>
                    <Label
                      value="PERCENTUAL IMPLEMENTADO"
                      offset={0}
                      angle={-90}
                      fontSize={10.5}
                      textAnchor="middle"
                      dx={-10}
                    />
                  </YAxis>

                  <Tooltip
                    formatter={(value, name, props) => {
                      if (name === i18next.t("total_implemented")) {
                        return [`${value}%`, name];
                      }
                      return [value, name];
                    }}
                  />

                  <Tooltip />
                  <Legend />
                  <Bar
                    dataKey="GOING"
                    name={i18next.t("status_options_on_time")}
                    barSize={20}
                    stackId={"a"}
                    fill="#FEF08A"
                    radius={[0, 0, 4, 4]}
                  />
                  <Bar
                    dataKey="FINISHED"
                    name={i18next.t("status_options_finished")}
                    barSize={20}
                    stackId={"a"}
                    fill="#82ca9d"
                    radius={[0, 0, 0, 0]}
                  />
                  <Bar
                    dataKey="NOT_STARTED"
                    name={i18next.t("status_options_not_started")}
                    barSize={20}
                    stackId={"a"}
                    fill="#808080"
                    radius={[4, 4, 0, 0]}
                  />
                  <Line
                    type="monotone"
                    dataKey="TOTAL_IMPLEMENTED"
                    name={i18next.t("total_implemented")}
                    stroke={`${pSBC(0.9, designSystem?.cssBasics.primaryColor)}`}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
